import { log as debugLog } from '@/utils/debugLogger';
import { waitFor } from './utils/waitFor';
import { synthesizeKanbanRoute } from './utils/synthesizeKanbanRoute';
import { probeTaskState } from './utils/probeTaskState';
import { ShareIntentError } from './errors';
import { taskBoardIntent } from './taskBoardIntent';
import { resolveTaskSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigation';
import { executeTagSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigationExecutor';

/**
 * The "task-expand" share intent: encode/decode a task into a deep-link URL
 * and reconstruct the fullscreen-viewer state on arrival as if the user had
 * clicked Isolate then Expand on TaskBoard.
 *
 * URL: `${origin}/${facilityId}/?intent=task-expand&taskId=${taskId}`
 *
 * Fullscreen 3D renders through ViewerTaskColoring/useSpatialFocus entity
 * scope. Fullscreen 360 mounts first, then the canonical executor owns exact
 * scan loading and focus.
 */
export const taskExpandIntent = {
  type: 'task-expand',

  /**
   * @param {{ facilityId: string, taskId: string, taskTypeId: string }} params
   * @returns {string}
   *
   * Note: taskTypeId is encoded in the URL because the task entity returned by
   * TASK/GET_BY_ID doesn't reliably expose it (varies by backend version), and
   * we need it BOTH for synthesizing the back-target Kanban route AND for the
   * landing-side TaskBoard data fetch. At encode time on the sender side, we
   * always have it from $route.params.taskTypeId.
   */
  encode({ facilityId, taskId, taskTypeId }) {
    return `${window.location.origin}/${facilityId}/?intent=task-expand&taskId=${taskId}&taskTypeId=${taskTypeId}`;
  },

  /**
   * @param {Record<string, string>} query
   * @returns {{ type: 'task-expand', taskId: string, taskTypeId: string } | null}
   */
  decode(query) {
    if (query?.intent !== 'task-expand' || !query?.taskId || !query?.taskTypeId) return null;
    return { type: 'task-expand', taskId: query.taskId, taskTypeId: query.taskTypeId };
  },

  /**
   * @param {{ taskId: string, taskTypeId: string }} params
   * @param {{ dispatch: Function, commit: Function, state: object, rootState: object }} ctx
   */
  async play({ taskId, taskTypeId, _probed }, { dispatch, commit, rootState }) {
    debugLog(`[taskExpand.play] starting taskId=${taskId} taskTypeId=${taskTypeId}`);

    // Wait for forge service ready — pre-register properties live on it.
    // Note: this happens BEFORE the probe even though the probe doesn't need
    // forgeService. We wait early so the post-probe viewer-setup code (which
    // does need it) doesn't have to wait again.
    const ready = await waitFor(() => !!rootState.forgeService);
    if (!ready) {
      throw new ShareIntentError('Forge service did not initialize', 'forge-not-ready');
    }

    // Probe task state: existence, tags, and viewer-feasibility at THIS
    // facility. Reuse delegating intent's probe via _probed if provided —
    // total cost across delegation chain is one probe, not two.
    const probe = _probed || (await probeTaskState(taskId, { dispatch, rootState }));

    // Hard-fail when the task itself doesn't exist. Task-board.play handles
    // this case by silently falling through to Kanban; task-expand is more
    // explicit (toast) because a fullscreen-viewer URL pointing to a deleted
    // task is genuinely unrecoverable from the user's POV — better signal.
    if (!probe.ok || !probe.task) {
      throw new ShareIntentError('Task not found', 'task-not-found');
    }

    // Strict fallback: anything that fails the visualizability test routes
    // to task-board. This catches BOTH "no tags" AND the user's reported
    // case "tags exist but at another facility / no harvest here." Bounded
    // delegation: task-board.play promotes only when canVisualize is true;
    // this branch implies !canVisualize, so it cannot ping-pong back to us.
    if (!probe.hasTags || !probe.canVisualize) {
      debugLog('[taskExpand.play] cannot visualize at current facility — falling back to task-board', {
        taskId,
        hasTags: probe.hasTags,
        canVisualize: probe.canVisualize,
      });
      return taskBoardIntent.play(
        { taskId, taskTypeId, _probed: probe },
        { dispatch, commit, rootState },
      );
    }

    // Resolve again at the execution boundary so a stale probe result cannot
    // steer a later viewer action after tag state has changed.
    const { task, fullTags } = probe;
    const plan = resolveTaskSpatialIntent(fullTags, {
      currentFacilityId: rootState.assetId,
      currentFacilityImageIds: (rootState.projects || [])
        .flatMap((project) => project.images || [])
        .map((image) => image.id),
      intent: 'task-focus',
    });
    if (plan.kind !== 'viewer') {
      // Mark the reused probe non-visualizable so task-board cannot promote
      // the now-stale task back into this intent.
      return taskBoardIntent.play(
        {
          taskId,
          taskTypeId,
          _probed: { ...probe, canVisualize: false, viewerState: null },
        },
        { dispatch, commit, rootState },
      );
    }
    const viewer = plan.viewer === '360' ? '360' : '3d';
    const viewerTab = viewer === '360' ? '360 View' : '3D View';
    const effectiveTagIds = probe.effectiveTagIds?.length
      ? probe.effectiveTagIds
      : task.forgeTagsIds;
    const executionTag = fullTags.find((tag) => (
      !Array.isArray(plan.includedTagIds) || plan.includedTagIds.includes(tag.id)
    )) || fullTags[0];
    debugLog(`[taskExpand.play] resolved viewer=${viewer} viewerTab=${viewerTab} reason=${plan.reason}`);

    // Cache the fetched task in TASK module so ViewerTaskColoring can find it
    // via state.entries.tasks.items without a heavy TASK/GET_DATA dispatch.
    // ViewerTaskColoring's setColoring filters this.tasks by isolatedTasks —
    // without this entry, the filter returns [] and no coloring applies.
    commit('TASK/UPSERT_TASK', task, { root: true });

    // Ensure isolation state is set so back-restore (pendingRestore consumer
    // in Task/views/index.vue) sees the right isolated tasks. Mirrors what
    // showTasksOn3d sets for the inline expand path.
    commit('TASK/SET_ISOLATED_TASKS', [task.id], { root: true });

    // Mount fullscreen before executing 360: the canonical primitive needs
    // pageView's panorama host to exist before it can create the viewer.
    await dispatch('VIEWERS_MODE/START_VIEWER_MODE', {
      mode: viewer === '3d' ? 'task-coloring' : 'dashboard-fullscreen',
      button: 'Back to Kanban',
      viewer,
      metadata: {
        taskTypeId, // from URL — authoritative
        viewerMode: 'tasks',
        taskRestore: { viewerTab, forgeTagsIds: effectiveTagIds },
      },
      panoramaTransfer: null,
    }, { root: true });

    if (viewer === '360') {
      try {
        const hostMounted = await waitFor(
          () => !!document.getElementById('panoramaContainer'),
          { timeout: 10000 },
        );
        if (!hostMounted) {
          throw new ShareIntentError('360 viewer did not mount', 'forge-not-ready');
        }
        const outcome = await executeTagSpatialIntent({
          plan,
          tag: executionTag,
          isolate: false,
          source: 'task-expand',
          requestedIntent: 'task-focus',
        });
        if (outcome?.status !== 'executed') {
          throw new ShareIntentError('Task spatial target is unavailable', 'tags-unavailable');
        }
      } catch (err) {
        await dispatch('VIEWERS_MODE/EXIT_VIEWER_MODE', null, { root: true });
        // EXIT normally promotes taskRestore into pendingRestore. This exit is
        // recovery from a viewer that never became actionable, so there is no
        // Task view to restore and no isolation selection to retain.
        commit('VIEWERS_MODE/setPendingRestore', null, { root: true });
        commit('TASK/SET_ISOLATED_TASKS', [], { root: true });
        throw err;
      }
    }

    // Override the auto-captured lastRoute (which would be the deep-link URL
    // itself — useless) with the synthetic Kanban route. When the user clicks
    // back, BACK_TO_PAGE pushes here; Task/views/index.vue's mount hook reads
    // VIEWERS_MODE.pendingRestore (set by EXIT_VIEWER_MODE from metadata.taskRestore)
    // and reconstructs the inline panel with the task isolated.
    //
    // synthesizeKanbanRoute uses URL-encoded taskTypeId rather than task.taskTypeId
    // because the task entity from GET_BY_ID doesn't reliably expose that field.
    const facilityId = rootState.assetId;
    commit('VIEWERS_MODE/setLastRoute', synthesizeKanbanRoute({ taskTypeId }, facilityId), { root: true });
    debugLog(`[taskExpand.play] lastRoute synthesized facility=${facilityId} taskTypeId=${taskTypeId}`);
  },
};
